<script setup lang="tsx">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { useStorage } from "@vueuse/core";
import { Column, ElButton, SortBy, TableV2SortOrder } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { ItemDefinition, ItemDefinitionListDto } from "~/api";
import ComplianceQuery from "~/Compliance/components/ComplianceQuery.vue";
import TableWrapper from "~/components/TableWrapper.vue";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { formatDate } from "~/utils/dateUtils";
import { enumDescriptions } from "~/utils/enumDescriptions";
import { useStableAutoResizer } from "~/utils/resizeObserverFix.simple";
import ItemDefinitionEditor from "../components/ItemDefinitionEditor.vue";

type listItemType = ItemDefinitionListDto;
type mainType = ItemDefinition;

const loading = ref(true);

const items = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();

// Prevent ResizeObserver performance issues
const { autoResizerRef } = useStableAutoResizer();

const refreshList = async () => {
  loading.value = true;
  apiClient.itemDefinition
    .getAllItemDefinitionDtos(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      items.value = res;
      // sortList();
      loading.value = false;
    });
};

const includePublicItems = useStorage("show-public-items", true);

onMounted(async () => {
  localStorage.setItem("editing-copied-item", JSON.stringify(false));

  await entityDefinitionStore.load();
  await refreshList();
});

const extractListItem = (item: mainType): listItemType => {
  // similar to creating the DTO on the server
  return {
    name: item.name,
    guid: item.guid,
    forEntityDefinitionGuid: item.forEntityDefinitionGuid,
    status: item.status,
    importance: item.importance,
    madePublicAt: item.madePublicAt,
  };
};

//*** start Sorting */
const sortState = ref<SortBy>({ key: 0, order: TableV2SortOrder.ASC });
const lastState = ref<SortBy>({ key: 0, order: TableV2SortOrder.DESC });
const onSort = (params: SortBy) => {
  sortState.value = params;
  sortResults();
};

const sortResults = () => {
  const { key, order } = sortState.value;
  if (key) {
    const delta = order === TableV2SortOrder.ASC ? 1 : -1;
    lastState.value = { key, order };
    items.value.sort((a, b) => {
      return (a[key as keyof ItemDefinitionListDto] ?? "") >
        (b[key as keyof ItemDefinitionListDto] ?? "")
        ? delta
        : -delta;
    });
  } else {
    items.value.sort((a, b) => {
      const nameA = a.name || "";
      const nameB = b.name || "";
      return nameA.localeCompare(nameB);
    });
  }
};

const sortList = () => {
  // to be improved or use Table2 sort
  // items.value.sort((a: listItemType, b: listItemType) => {
  //   return (a.name ?? "").localeCompare(b.name ?? "");
  // });
};

//*** end Sorting */

let numCreated = 0;
const createItem = () => {
  selectedRow.value = { guid: "new" + numCreated++ } as listItemType;
  drawerOpen.value = true;
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const handleNewItemAdded = (item: mainType) => {
  items.value.push(extractListItem(item));
  sortList();
};

const handleItemUpdated = (item: mainType) => {
  const index = items.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    items.value[index] = extractListItem(item);
  }
  sortList();
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

/*** for querying/filtering the listing ***/
// Need to fix
const complianceQueryInput = ref<{
  type: string[];
  importance: string[];
  status: string[];
  search: "";
}>({
  type: [],
  importance: [],
  status: [],
  search: "",
});

const itemsFiltered = computed(() => {
  const searchFiltered = complianceQueryInput.value.search !== "";
  const typeFiltered = complianceQueryInput.value.type.length !== 0;
  const importanceFiltered = complianceQueryInput.value.importance.length !== 0;
  const statusFiltered = complianceQueryInput.value.status.length !== 0;

  return items.value.filter((item) => {
    if (searchFiltered) {
      if (
        !item.name?.toLowerCase().includes(complianceQueryInput.value.search)
      ) {
        return false;
      }
    }
    if (typeFiltered) {
      if (
        item.forEntityDefinitionGuid &&
        !complianceQueryInput.value.type.includes(item.forEntityDefinitionGuid)
      ) {
        return false;
      }
    }
    if (importanceFiltered) {
      if (
        item.importance &&
        !complianceQueryInput.value.importance.includes(item.importance)
      ) {
        return false;
      }
    }
    if (statusFiltered) {
      if (
        item.status &&
        !complianceQueryInput.value.status.includes(item.status)
      ) {
        return false;
      }
    }
    if (!includePublicItems.value) {
      if (item.madePublicAt !== null) {
        return false;
      }
    }
    return true;
  });
});
// const handleEditItemDefinition = (formGuid: string, itemGuid: string) => {
//   router.push(
//     applicationRouteMap.Compliance.children.ItemDesigner.path
//       .replace(":formGuid", formGuid)
//       .replace(":itemGuid", itemGuid),
//   );
// };
// /**
//  * Creates a new item definition then redirects to the edit page
//  * for it.
//  */
// const handleCreateItemDefinition = async () => {
//   ElMessageBox.prompt(
//     "Please enter the name of this compliance item:",
//     "Create New Compliance Item",
//     {
//       confirmButtonText: "Create",
//       cancelButtonText: "Cancel",
//     },
//   )
//     .then(async ({ value }) => {
//       const newItem: ComplianceItemDefinitionCreateDto = {
//         name: value,
//         type: ComplianceItemType.PERSON,
//         status: ComplianceItemStatus.ACTIVE,
//       };
//       await apiClient.complianceItemDefinition
//         .createComplianceItemDefinition(newItem)
//         .then((res) => {
//           itemDefinitionList.value.push(res);
//           handleEditItemDefinition(
//             res.formDefinitionGuid ?? "",
//             res.guid ?? "",
//           );
//         });
//     })
//     .catch(() => {
//       ElMessage({
//         type: "info",
//         message: "Canceled",
//       });
//     });
// };

const tempColumns: Column<any>[] = [
  {
    title: "Compliance Item",
    key: "name",
    dataKey: "name",
    width: 300,

    cellRenderer: (row: any) => {
      return (
        <ElButton
          text
          onClick={() => showItem(row.rowData)}
          icon={Fold}
          type="primary"
          style="font-size: 15px;"
        >
          {row.rowData.name}
        </ElButton>
      );
    },
  },
  {
    title: "Entity",
    key: "forEntityDefinitionGuid",
    dataKey: "forEntityDefinitionGuid",
    width: 200,
    cellRenderer: (row: any) => {
      return (
        <span>
          {entityDefinitionStore.nameOfType(
            row.rowData.forEntityDefinitionGuid ?? undefined,
          )}
        </span>
      );
    },
  },
  {
    title: "Status",
    key: "status",
    dataKey: "status",
    width: 200,
    cellRenderer: (row: any) => {
      const status = row.rowData.status;
      const statusDescription =
        enumDescriptions.complianceItemStatus[
          status as keyof typeof enumDescriptions.complianceItemStatus
        ];

      return <span>{statusDescription ? statusDescription.label : ""}</span>;
    },
  },
  {
    title: "Importance",
    key: "importance",
    dataKey: "importance",
    width: 200,
  },
  {
    title: "Public",
    key: "madePublicAt",
    dataKey: "madePublicAt",
    align: "center",
    width: 100,
    cellRenderer: (row: any) => {
      return (
        <span title={formatDate(row.rowData.madePublicAt, 1)}>
          {row.rowData.madePublicAt ? "Public" : ""}
        </span>
      );
    },
  },
];

const columns = computed(() =>
  tempColumns.map((col) => {
    return {
      ...col,
      sortable: true,
    };
  }),
);
</script>

<template>
  <section>
    <!-- <ComplianceItemDefinitionEditor
      :key="itemDefinition?.guid"
      v-model="editItemDefinitionDrawerIsOpen"
      v-model:compliance-item="itemDefinition"
      :is-creating-item="isCreatingItem"
      @add:compliance-item="refreshComplianceItemDefinitions()"
      @update:compliance-item="refreshComplianceItemDefinitions()"
    /> -->
    <div class="flexRow">
      <span class="showingInTable">
        Showing {{ itemsFiltered.length }} of
        {{ items.length }}
      </span>
      <RefreshButton @click="refreshList" />
      <ComplianceQuery @submit:query="complianceQueryInput = $event">
        <template #create-button> </template>
      </ComplianceQuery>
      <el-checkbox v-model="includePublicItems" label="Include public items" />

      <el-button
        v-if="myInfoStore.hasSetupBasicRole"
        :icon="CirclePlus"
        title="Create a new item definition"
        class="addButton"
        @click="createItem"
      >
        New Item
      </el-button>
    </div>
  </section>

  <!-- TABLE FOR COMPLIANCE ITEMS -->
  <ElAutoResizer ref="autoResizerRef" v-loading="loading">
    <template #default="{ height, width }">
      <TableWrapper
        v-if="!loading"
        ref="mainTableRef"
        :columns="columns"
        :data="itemsFiltered"
        row-key="guid"
        :height="height"
        :width="width"
        :sort-state="sortState"
        @column-sort="onSort"
      >
      </TableWrapper>
    </template>
  </ElAutoResizer>

  <ItemDefinitionEditor
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :item-definition-list-dto="selectedRow"
    @close-drawer="closeDrawer"
    @added="handleNewItemAdded"
    @updated="handleItemUpdated"
    @deleted="refreshList"
  ></ItemDefinitionEditor>
</template>

<style lang="less"></style>
